import { FunctionComponent, useMemo, useRef } from 'react';
import {
  DatetimeChangeEventDetail,
  IonDatetime,
  IonDatetimeButton,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonButton,
  IonIcon,
} from '@ionic/react';
import {
  AttributeConditionPresenter,
  AttributeDatePresenter,
  AttributeNumberPresenter,
  AttributePresenter,
  getMapAttributeConditionLabel,
} from '../presenters/attribute.presenter';
import { AttributeCondition } from '../models/attribute.model';
import { IonDatetimeCustomEvent } from '@ionic/core/dist/types/components';
import { useTranslation } from 'react-i18next';
import { closeOutline } from 'ionicons/icons';

export const EditableAttribute: FunctionComponent<{
  isPlanExported: boolean;
  attribute: AttributePresenter;
  onAttributeValueChanged: (newValue: any) => Promise<void>;
}> = ({ isPlanExported, attribute, onAttributeValueChanged }) => {
  const { t } = useTranslation();
  const mapAttributeConditionLabel = useMemo(() => getMapAttributeConditionLabel(t), [t]);
  const modalRef = useRef<HTMLIonModalElement>(null);

  const onDateTimeValueChanged = async (e: IonDatetimeCustomEvent<DatetimeChangeEventDetail>) => {
    if (e.detail.value && !Array.isArray(e.detail.value)) {
      const [year, month, day] = e.detail.value.split('-');
      await onAttributeValueChanged([day, String(month).padStart(2, '0'), year].join('/'));
      await modalRef.current?.dismiss();
    }
  };

  const getDefaultDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const openDatePicker = async () => {
    const defaultDate = getDefaultDate();
    await onAttributeValueChanged(defaultDate);
    modalRef.current?.present();
  };

  const clearDate = async () => {
    await onAttributeValueChanged('');
  };

  const input =
    attribute instanceof AttributeDatePresenter ? (
      <>
        {attribute.value ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <IonDatetimeButton datetime={attribute.id} disabled={isPlanExported ? isPlanExported : !attribute.isEditable}/>
            <IonButton
              fill="clear"
              size="small"
              disabled={isPlanExported ? isPlanExported : !attribute.isEditable}
              onClick={clearDate}
              style={{ margin: 0, minWidth: 'auto' }}
            >
              <IonIcon icon={closeOutline} style={{ color: 'gray' }} />
            </IonButton>
          </div>
        ) : (
          <IonButton
            disabled={isPlanExported ? isPlanExported : !attribute.isEditable}
            onClick={openDatePicker}
          >
            {t('plan.attributesView.selectDate')}
          </IonButton>
        )}
        <IonModal ref={modalRef} keepContentsMounted={true}>
          <IonDatetime
            color={'red'}
            presentation={'date'}
            firstDayOfWeek={1}
            id={attribute.id}
            value={attribute.value || getDefaultDate()}
            onIonChange={onDateTimeValueChanged}
          />
        </IonModal>
      </>
    ) : attribute instanceof AttributeNumberPresenter ? (
      <IonInput disabled={ isPlanExported ? isPlanExported : !attribute.isEditable }
                debounce={ 500 }
                value={ attribute.value }
                type={ 'number' }
                onIonChange={ (e) => onAttributeValueChanged(e.detail.value) }
      />
    ) : attribute instanceof AttributeConditionPresenter ? (
      <IonSelect disabled={ isPlanExported ? isPlanExported : !attribute.isEditable }
                 interface="action-sheet"
                 interfaceOptions={ {
                   header: attribute.name,
                   subHeader: t('plan.attributesView.conditionSubHeader'),
                 } }
                 value={ `${ attribute.value }` }
                 onIonChange={ (e) => onAttributeValueChanged(e.detail.value) }
                 style={ { maxWidth: '100%' } }
      >
        <IonSelectOption value={ `${ AttributeCondition.UNDEFINED }` }>
          { mapAttributeConditionLabel[AttributeCondition.UNDEFINED] }
        </IonSelectOption>
        <IonSelectOption value={ `${ AttributeCondition.BAD }` }>
          { mapAttributeConditionLabel[AttributeCondition.BAD] }
        </IonSelectOption>
        <IonSelectOption value={ `${ AttributeCondition.AVERAGE_BAD }` }>
          { mapAttributeConditionLabel[AttributeCondition.AVERAGE_BAD] }
        </IonSelectOption>
        <IonSelectOption value={ `${ AttributeCondition.AVERAGE_GOOD }` }>
          { mapAttributeConditionLabel[AttributeCondition.AVERAGE_GOOD] }
        </IonSelectOption>
        <IonSelectOption value={ `${ AttributeCondition.GOOD }` }>
          { mapAttributeConditionLabel[AttributeCondition.GOOD] }
        </IonSelectOption>
      </IonSelect>

    ) : attribute.domainValues.length > 0 ? (
      <IonSelect disabled={ isPlanExported ? isPlanExported : !attribute.isEditable }
                 interface="action-sheet"
                 interfaceOptions={ {
                   header: attribute.name,
                 } }
                 value={ `${ attribute.value }` }
                 onIonChange={ (e) => onAttributeValueChanged(e.detail.value) }
                 style={ { maxWidth: '100%' } }
      >
        { attribute.domainValues.map((domainValue) => (
          <IonSelectOption key={ domainValue.rank } value={ domainValue.value }>
            { domainValue.value }
          </IonSelectOption>
        )) }
      </IonSelect>
    ) : (
      <IonInput debounce={ 500 }
                value={ attribute.value }
                readonly={ isPlanExported ? isPlanExported : !attribute.isEditable }
                style={ { color: attribute.isEditable ? undefined : 'gray' } }
                onIonChange={ (e) => onAttributeValueChanged(e.detail.value) }/>
    );
  return (
    <IonItem role="listitem">
      <IonLabel style={ { opacity: 1 } } position={ 'fixed' } slot="start" className={ 'ion-text-wrap' }>
        { attribute.name }
      </IonLabel>
      <div style={ {
        border: attribute.isEditable ? '1px solid gray' : '1px solid lightgray',
        borderRadius: 5,
        margin: '7px 0',
        flex: attribute instanceof AttributeDatePresenter ? undefined : 1,
        paddingLeft: attribute instanceof AttributeDatePresenter ? 0 : 3,
        paddingRight: attribute instanceof AttributeDatePresenter ? 0 : 10
      } }>
        { input }
      </div>
    </IonItem>
  );
};
